import { effect, inject, Injectable, linkedSignal, signal } from '@angular/core';
import { MetadataService } from '../../services/metadata/metadata';
import { OfflineSessionService } from '../../services/offline/offline-session';

/**
 * Token this tab reads with, kept per tab: the `token` cookie is shared by every tab and belongs to
 * the last book opened, so a reload that trusted it could open the book of another tab.
 */
export const TAB_TOKEN_SESSION_KEY = 'nr:tab-token';

export function readTabToken(): string | null {
  try {
    return sessionStorage.getItem(TAB_TOKEN_SESSION_KEY);
  } catch {
    return null;
  }
}

function rememberTabToken(token: string): void {
  try {
    sessionStorage.setItem(TAB_TOKEN_SESSION_KEY, token);
  } catch {
    // Without session storage a reload falls back to the cookie, as before.
  }
}

@Injectable({
  providedIn: 'root',
})
export class AuthStore {
  private readonly metadataService = inject(MetadataService);
  private readonly offlineSession = inject(OfflineSessionService);
  readonly token = linkedSignal<string, string>({
    source: () => this.metadataService.metadata()?.token ?? '',
    computation: (newToken, previous) => {
      if (!newToken && previous?.value) return previous.value;
      return newToken;
    },
  });
  readonly cipherKey = signal<string>('');

  constructor() {
    effect(() => {
      const t = this.token();
      if (t) rememberTabToken(t);
      // Wait until the open succeeds online: an offline session only has stale tokens, and
      // writing one would break the online tabs of this origin, which share the cookie.
      if (t && this.offlineSession.mode() === 'online') cookieStore.set('token', t);
    });
  }
}
